import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  fuzzyMatch,
  ScoredQuestion
} from '@com-delta-idp/mnx-sitewide-shared-utils';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { EndpointKeys, IDP_APPS } from '../../constants/utils/app.constants';
import {
  ApprovedQuestion,
  ApprovedQuestionsResponse
} from '../../models/site-search/question-bank.interfaces';
import { AppConfigService } from '../utils';

@Injectable({ providedIn: 'root' })
export class QuestionBankService implements OnDestroy {
  private static readonly DEBOUNCE_MS = 200;
  private static readonly DEFAULT_LIMIT = 8;

  private questionsSubject = new BehaviorSubject<ApprovedQuestion[]>([]);
  private searchTerm$ = new Subject<string>();
  private searchResults$ = new BehaviorSubject<string[]>([]);
  private destroy$ = new Subject<void>();
  private loaded = false;

  approvedQuestions$ = this.questionsSubject.asObservable();
  filteredResults$ = this.searchResults$.asObservable();

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService
  ) {
    this.initSearchPipeline();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Fetches approved questions once per session. Subsequent calls return cached data. */
  getApprovedQuestions(languageCode?: string): Observable<ApprovedQuestion[]> {
    if (this.loaded) {
      return of(this.questionsSubject.getValue());
    }
    return this.fetchFromApi(languageCode);
  }

  /** Triggers debounced fuzzy search. Results emitted via filteredResults$. */
  search(term: string): void {
    this.searchTerm$.next(term);
  }

  /** Synchronous fuzzy search against cached questions. */
  searchQuestions(term: string, limit?: number): string[] {
    const questions = this.questionsSubject.getValue();
    const max = limit || QuestionBankService.DEFAULT_LIMIT;
    if (!term.trim()) return questions.slice(0, max).map((q) => q.questionText);
    const texts = questions.map((q) => q.questionText);
    const scored: ScoredQuestion[] = fuzzyMatch(term, texts, max);
    return scored.map((s) => s.questionText);
  }

  private initSearchPipeline(): void {
    this.searchTerm$
      .pipe(
        debounceTime(QuestionBankService.DEBOUNCE_MS),
        distinctUntilChanged(),
        map((term) => this.searchQuestions(term)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => this.searchResults$.next(results));
  }

  private fetchFromApi(languageCode?: string): Observable<ApprovedQuestion[]> {
    return this.appConfig
      .getConfigValue(IDP_APPS.SITE_SEARCH, EndpointKeys.AI_SEARCH_REST_API)
      .pipe(
        take(1),
        switchMap((baseUrl) => {
          if (!baseUrl) {
            this.loaded = true;
            this.questionsSubject.next([]);
            return of([] as ApprovedQuestion[]);
          }
          let params = new HttpParams();
          if (languageCode) params = params.set('lang', languageCode);
          return this.http
            .get<ApprovedQuestionsResponse>(
              `${baseUrl}/question-bank/questions/approved`,
              { params }
            )
            .pipe(map((response) => response.questions));
        }),
        tap((questions) => {
          this.questionsSubject.next(questions);
          this.loaded = true;
        }),
        catchError(() => {
          this.questionsSubject.next([]);
          this.loaded = true;
          return of([]);
        })
      );
  }
}
